import React, { useMemo, useState, useEffect } from 'react';
import { MantineReactTable } from 'mantine-react-table';
import { useTheme } from '../../ThemeContext'; // Adjust the path as necessary
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { MantineProvider, Loader, Select } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns'; // Import date-fns for date formatting
import { getAllMeeting, meetingStatus } from '../../api/api'; // Adjust import if needed
import Comments from '../Comments/AddComments';
import { toast } from 'react-toastify';

// Utility function for formatting date
const formatDate = (date, formatStr = 'dd-MM-yyyy hh:mm a') => {
  try {
    return format(new Date(date), formatStr);
  } catch (error) {
    console.error('Invalid date provided:', date);
    return 'Invalid Date';
  }
};

export const Example = ({ handlePageUpdate, updatePage }) => {
  const navigate = useNavigate();
  const { theme } = useTheme(); // Get the theme from context
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // Track loading state
  const [isCommentOpen, setIsCommentOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  useEffect(() => {
    const fetchMeetings = async () => {
      try {
        setIsLoading(true); // Start loading
        const response = await getAllMeeting();
        // console.log(response.data);
        setData(response.data); // Set fetched data
      } catch (error) {
        console.error('Error fetching meetings:', error);
      } finally {
        setIsLoading(false); // Stop loading
      }
    };

    fetchMeetings();
  }, [updatePage]);

  // Function to handle status change
  const handleStatusChange = async (row, status) => {
    try {
      // Send API request to update the status of the meeting
      const mStatus = {
        meetingId: row.original._id,
        status: status,
      };

      // Call the API to update the status
      const response = await meetingStatus(mStatus);

      // If the request was successful, update the status locally in the data state
      if (response?.data) {
        console.log(response.data);
        toast.success('Meeting status updated successfully');
        handlePageUpdate();
      }
    } catch (error) {
      toast.error(error.response.data.message);
      console.error('Error changing status:', error);
    }
  };

  // Function to open comment modal and pass meetingId and projectId
  const openComment = (row) => {
    setRowToDelete(row.original);
    setIsCommentOpen(true);
  };

  // Columns definition for the table
  const columns = useMemo(
    () => [
      {
        accessorKey: 'projectId.project_name',
        header: 'Project',
      },
      {
        accessorKey: 'agenda',
        header: 'Agenda',
        Cell: ({ cell }) => cell.getValue() || 'N/A',
      },
      {
        accessorKey: 'dateTime',
        header: 'Meeting Time',
        Cell: ({ row }) => {
          const dateTime = row.original.dateTime;
          return dateTime ? formatDate(dateTime, 'dd-MM-yyyy hh:mm a') : 'N/A';
        },
      },
      // {
      //   accessorKey: 'participants',
      //   header: 'People Involved',
      //   Cell: ({ row }) => (row.original.people_involved || []).join(', ') || 'N/A',
      // },
      {
        accessorKey: 'location',
        header: 'Location',
        Cell: ({ cell }) => cell.getValue() || 'N/A',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        Cell: ({ row }) => {
          const status = row.getValue('status');
          return (
            <Select
              placeholder="Select Status"
              value={status}
              onChange={(value) => handleStatusChange(row, value)}
              searchable
              data={[
                { value: 'Upcoming', label: 'Upcoming' },
                { value: 'Completed', label: 'Completed' },
                { value: 'Cancelled', label: 'Cancelled' },
              ]}
              styles={{
                input: {
                  fontSize: '0.875rem',
                  padding: '5px 10px',
                  color:
                    status === 'Completed'
                      ? 'green'
                      : status === 'Cancelled'
                      ? 'red'
                      : 'blue',
                },
                dropdown: { backgroundColor: '#2d3748' },
              }}
              dropdownPosition="bottom"
              transition="pop-top-left"
              transitionDuration={150}
              withinPortal
            />
          );
        },
      },
      {
        header: 'Actions',
        Cell: ({ row }) => (
          <div className="relative">
            <ChatBubbleLeftRightIcon
              className="w-5 h-5 text-blue-600 hover:text-red-800 cursor-pointer"
              onClick={() => openComment(row)}
            />
          </div>
        ),
      },
    ],
    [theme]
  );

  const table = useMemo(
    () => ({
      columns,
      data,
      enableColumnActions: true,
      enableColumnFilters: true,
      enablePagination: true,
      enableSorting: true,
      enableBottomToolbar: true,
      enableRowNumbers: true,
      enableGlobalFilter: true,
      mantineTableProps: {
        highlightOnHover: true,
        withBorder: true,
        sx: {
          'thead > tr': {
            backgroundColor: theme === 'dark' ? '#1a202c' : '#f7fafc',
          },
          'thead > tr > th': {
            backgroundColor: theme === 'dark' ? '#2d3748' : '#edf2f7',
            color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
          },
          'tbody > tr': {
            '&:hover': {
              backgroundColor: theme === 'dark' ? '#2d3748' : '#f1f5f9',
            },
          },
          'tbody > tr > td': {
            backgroundColor: theme === 'dark' ? '#2d3748' : '#ffffff',
            color: theme === 'dark' ? '#ffff' : '#333333',
          },
        },
      },
    }),
    [data, theme]
  );

  return (
    <div>
      <MantineProvider theme={{ colorScheme: theme }}>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '50px' }}>
            <Loader size="lg" />
          </div>
        ) : (
          <MantineReactTable {...table} />
        )}
        {isCommentOpen && rowToDelete && rowToDelete.projectId && (
          <Comments
            isOpen={isCommentOpen}
            setIsOpen={setIsCommentOpen}
            meetingId={rowToDelete._id} // Pass meetingId here
            projectId={rowToDelete.projectId._id} // Pass projectId here
            type={'meetings'}
            name={'Minutes of Meetings'}
          />
        )}
      </MantineProvider>
    </div>
  );
};

export default Example;
